import type { BaseUser, Device, Document, Folder } from "@models";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import type { SvgIconProps } from "@mui/material";
import type { DocStatus, Template } from "@types";
import type { ComponentType } from "react";
import { sortAlphabetically } from "src/components/Documents/DocumentsPageSorting";
import {
  ApprovedIcon,
  DraftIcon,
  UnapprovedIcon,
} from "src/components/DocumentStatusFileIcons";
import {
  buildDeviceNameById,
  dedupeDocumentsById,
  getDocumentCreatedBy,
  getDocumentIdentifier,
  getLatestDocumentStatus,
  getLatestDocumentVersion,
  getNavigableDocumentLabel,
  getVisibleNavigableDocuments,
  isDocumentVersionRouting,
} from "src/utils/documents";
import { getUserDisplayName } from "src/utils/user";
import { formatDocumentTableDate } from "./DocumentsTableDates";

type DocumentsPageItemBase = {
  id: string;
  name: string;
  revisionNumber: number | null;
  status: string | null;
  statusValue: DocStatus | null;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  documentIdentifier: string | null;
  scope: "Global" | string | null;
  type: "Document" | string | "Folder";
  icon: ComponentType<SvgIconProps>;
  isRouting: boolean;
};

type DocumentsPageFolderItem = DocumentsPageItemBase & {
  kind: "folder";
  folder: Folder;
  folderId: null;
  parentFolderId: string | null;
};

type DocumentsPageDocumentItem = DocumentsPageItemBase & {
  kind: "document";
  document: Document;
  folderId: string | null;
  parentFolderId: null;
};

export type DocumentsPageItem =
  | DocumentsPageFolderItem
  | DocumentsPageDocumentItem;

type BuildDocumentsPageFullListArgs = {
  devices: Pick<Device, "id" | "name">[];
  documents: Document[];
  folders: Folder[];
  resolveTemplateForDocument: (document: Document) => Template | undefined;
  users?: Pick<BaseUser, "id" | "firstName" | "lastName">[];
};

type FolderCreatorUser = Pick<BaseUser, "id" | "firstName" | "lastName">;

const docStatusDictionary: Record<
  DocStatus,
  {
    label: string;
    icon: ComponentType<SvgIconProps>;
  }
> = {
  DRAFT: {
    label: "Draft",
    icon: DraftIcon,
  },
  UNAPPROVED: {
    label: "Unapproved",
    icon: UnapprovedIcon,
  },
  LIVE: {
    label: "Live",
    icon: ApprovedIcon,
  },
  AVAILABLE: {
    label: "Available",
    icon: DraftIcon,
  },
  ARCHIVED: {
    label: "Archived",
    icon: DraftIcon,
  },
};

const commonMimeTypeLabels: Record<string, string> = {
  "application/csv": "CSV",
  "application/gzip": "GZ",
  "application/json": "JSON",
  "application/msword": "DOC",
  "application/pdf": "PDF",
  "application/rtf": "RTF",
  "application/vnd.ms-excel": "XLS",
  "application/vnd.ms-powerpoint": "PPT",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "PPTX",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "XLSX",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "DOCX",
  "application/xml": "XML",
  "image/jpeg": "JPEG",
  "image/png": "PNG",
  "text/csv": "CSV",
  "text/html": "HTML",
  "text/markdown": "MD",
  "text/plain": "TXT",
  "text/xml": "XML",
};

const getDocumentMimeTypeLabel = (mimeType: string) =>
  commonMimeTypeLabels[mimeType.toLowerCase()];

const getDocumentType = (
  document: Document
): DocumentsPageDocumentItem["type"] => {
  if (document.userUpload) {
    const mimeType = getLatestDocumentVersion(document)?.file?.mimeType;

    return mimeType
      ? getDocumentMimeTypeLabel(mimeType) ?? "Document"
      : "Document";
  }

  return "Document";
};

const getDocumentScope = ({
  deviceNameById,
  document,
}: {
  deviceNameById: Map<string, string>;
  document: Document;
}): DocumentsPageDocumentItem["scope"] => {
  if (!document.deviceId) {
    return "Global";
  }

  return deviceNameById.get(document.deviceId) ?? "Device";
};

const getParentFolderId = ({
  folder,
  folderIds,
}: {
  folder: Folder;
  folderIds: Set<string>;
}) => {
  const parentFolderId = folder.parentFolderId ?? null;

  if (
    !parentFolderId ||
    parentFolderId === folder.id ||
    !folderIds.has(parentFolderId)
  ) {
    return null;
  }

  return parentFolderId;
};

const getRequiredFolderCreatorUser = ({
  creatorUserId,
  folderId,
  usersById,
}: {
  creatorUserId: string;
  folderId: string;
  usersById: Map<string, FolderCreatorUser>;
}) => {
  const user = usersById.get(creatorUserId);

  if (!user) {
    throw new Error(
      `Missing creator user ${creatorUserId} for folder ${folderId}`
    );
  }

  return user;
};

const buildFolderItem = ({
  createdBy,
  folder,
  folderIds,
}: {
  createdBy: string;
  folder: Folder;
  folderIds: Set<string>;
}): DocumentsPageFolderItem => ({
  id: folder.id,
  kind: "folder",
  folder,
  name: folder.name,
  revisionNumber: null,
  status: null,
  statusValue: null,
  createdBy,
  createdAt: folder.createdAt,
  updatedAt: folder.updatedAt,
  documentIdentifier: null,
  scope: null,
  type: "Folder",
  icon: FolderOpenRoundedIcon,
  isRouting: false,
  folderId: null,
  parentFolderId: getParentFolderId({ folder, folderIds }),
});

const buildDocumentItem = ({
  deviceNameById,
  document,
  folderIds,
  resolveTemplateForDocument,
}: {
  deviceNameById: Map<string, string>;
  document: Document;
  folderIds: Set<string>;
  resolveTemplateForDocument: (document: Document) => Template | undefined;
}): DocumentsPageDocumentItem[] => {
  const latestVersion = getLatestDocumentVersion(document);
  const name = getNavigableDocumentLabel(document);

  if (!latestVersion || !name) {
    return [];
  }

  const createdAt = latestVersion.createdAt;
  const status = getLatestDocumentStatus(document);
  const templateConfig = document.templateKey
    ? resolveTemplateForDocument(document)
    : undefined;
  const documentIdentifier = templateConfig
    ? getDocumentIdentifier(
        document,
        latestVersion,
        document.versions,
        templateConfig
      )
    : null;
  const isRouting = isDocumentVersionRouting({
    documentVersion: latestVersion,
    status,
  });

  return [
    {
      id: document.id,
      kind: "document",
      document,
      name,
      revisionNumber: document?.latestRevisionNumber ?? null,
      status: docStatusDictionary[status].label,
      statusValue: status,
      createdBy: getDocumentCreatedBy(latestVersion),
      createdAt,
      updatedAt: latestVersion.updatedAt || createdAt,
      documentIdentifier,
      scope: getDocumentScope({ deviceNameById, document }),
      type: getDocumentType(document),
      icon: docStatusDictionary[status].icon,
      isRouting,
      folderId:
        document.folderId && folderIds.has(document.folderId)
          ? document.folderId
          : null,
      parentFolderId: null,
    },
  ];
};

export const buildDocumentsPageFullList = ({
  devices,
  documents,
  folders,
  resolveTemplateForDocument,
  users = [],
}: BuildDocumentsPageFullListArgs): DocumentsPageItem[] => {
  const deviceNameById = buildDeviceNameById(devices);
  const folderIds = new Set(folders.map((folder) => folder.id));
  const usersById = new Map(users.map((user) => [user.id, user]));
  const visibleDocuments = getVisibleNavigableDocuments({
    documents,
    resolveTemplateForDocument,
    templatesConfig: undefined,
  });
  const folderItems = sortAlphabetically(
    folders.map((folder) => {
      const createdBy = folder.createdBy
        ? getUserDisplayName(
            getRequiredFolderCreatorUser({
              creatorUserId: folder.createdBy,
              folderId: folder.id,
              usersById,
            })
          )
        : "Dovetail";

      return buildFolderItem({ createdBy, folder, folderIds });
    })
  );
  const documentItems = sortAlphabetically(
    dedupeDocumentsById(visibleDocuments).flatMap((document) =>
      buildDocumentItem({
        deviceNameById,
        document,
        folderIds,
        resolveTemplateForDocument,
      })
    )
  );

  return [...folderItems, ...documentItems];
};

export const getDocumentsPageDisplayContent = ({
  folderId,
  fullList,
}: {
  folderId: string | null;
  fullList: DocumentsPageItem[];
}) => {
  const selectedFolderExists =
    folderId !== null &&
    fullList.some((item) => item.kind === "folder" && item.id === folderId);
  const selectedParentFolderId = selectedFolderExists ? folderId : null;

  const folders = fullList.filter(
    (item): item is DocumentsPageFolderItem =>
      item.kind === "folder" && item.parentFolderId === selectedParentFolderId
  );
  const documents = fullList.filter(
    (item): item is DocumentsPageDocumentItem =>
      item.kind === "document" && item.folderId === selectedParentFolderId
  );

  return [...folders, ...documents];
};

const getRevisionSearchLabel = (revisionNumber: number | null) =>
  revisionNumber === null ? null : `R${revisionNumber + 1}`;

const getDocumentsPageItemSearchText = (item: DocumentsPageItem) =>
  [
    item.name.trim() || "No name",
    item.kind,
    item.status,
    getRevisionSearchLabel(item.revisionNumber),
    item.createdBy,
    formatDocumentTableDate(item.createdAt),
    formatDocumentTableDate(item.updatedAt),
    item.scope,
    item.type,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ");

export const filterDocumentsPageItemsBySearch = ({
  items,
  search,
}: {
  items: DocumentsPageItem[];
  search: string;
}) => {
  const normalizedSearch = search.trim().toLowerCase();

  if (!normalizedSearch) {
    return items;
  }

  return items.filter((item) =>
    getDocumentsPageItemSearchText(item)
      .toLowerCase()
      .includes(normalizedSearch)
  );
};
